import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { useToast } from "@/components/feature/ToastProvider";
import { useAuth } from "@/components/feature/AuthProvider";
import type { VoteDetail, VoteQuestion } from "@/lib/types";
import { submitVote } from "../api";
import {
  STATE_BADGE,
  STATE_LABEL,
  formatCountdown,
  formatDateTime,
  typeLabel,
} from "../utils";
import VoteQuestionBlock, {
  type AnswerValue,
} from "../components/VoteQuestionBlock";
import VoteResult from "../components/VoteResult";

const EMPTY_ANSWER: AnswerValue = { opts: [], text: "", score: 0 };

function buildInitial(questions: VoteQuestion[]): Record<number, AnswerValue> {
  const map: Record<number, AnswerValue> = {};
  questions.forEach((q) => {
    map[q.no] = {
      opts: q.my?.opts ?? [],
      text: q.my?.text ?? "",
      score: q.my?.score ?? 0,
    };
  });
  return map;
}

// 제출 전 필수/개수 검증
function validate(
  questions: VoteQuestion[],
  values: Record<number, AnswerValue>
): string | null {
  for (const q of questions) {
    const v = values[q.no] ?? EMPTY_ANSWER;
    const needReq = q.is_req === 1;
    if (q.qtype === "single" || q.qtype === "multi") {
      if (needReq && v.opts.length === 0) return `${q.title} 을(를) 선택해 주세요.`;
      if ((q.max_pick ?? 0) > 0 && v.opts.length > (q.max_pick ?? 0))
        return `${q.title} 은(는) 최대 ${q.max_pick}개까지 선택할 수 있습니다.`;
    } else if (q.qtype === "text") {
      if (needReq && !v.text.trim()) return `${q.title} 을(를) 입력해 주세요.`;
    } else if (q.qtype === "scale") {
      if (needReq && !v.score) return `${q.title} 을(를) 선택해 주세요.`;
    }
  }
  return null;
}

export default function VoteDetailPage() {
  const { no } = useParams();
  const { toast } = useToast();
  const { member, requireLogin } = useAuth();
  const now = useNow();

  const { data, loading, error, reload, serverTs } = useApi<VoteDetail>(
    no ? `votes.php?no=${no}` : null
  );

  const [values, setValues] = useState<Record<number, AnswerValue>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const questions = useMemo(() => data?.questions ?? [], [data]);
  const questionsKey = useMemo(() => questions.map((q) => q.no).join(","), [questions]);

  // 로드 / 재로드 시 «이미 고른 것» 을 미리 체크해 둔다
  useEffect(() => {
    if (data) {
      setValues(buildInitial(data.questions ?? []));
      setFormError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, questionsKey]);

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;
  const endRemaining = Math.max(0, (data?.end_in ?? 0) - elapsed);
  const startRemaining = Math.max(0, (data?.start_in ?? 0) - elapsed);

  const open = data?.state === "open";
  const joined = data?.is_joined === 1;
  const canChange = data?.is_change === 1;
  const canResult = data?.can_result === 1;
  const showForm = !!open && (!joined || canChange);
  const typeTxt = data ? typeLabel(data.vtype, data.type_txt) : "투표";

  const handleSubmit = useCallback(async () => {
    if (!no || !data) return;
    // 로그인 회원 전용이면 먼저 로그인 유도
    if (data.is_login === 1 && !member) {
      requireLogin(`/vote/${no}`);
      return;
    }
    const msg = validate(questions, values);
    if (msg) {
      setFormError(msg);
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const fields: [string, string][] = [];
      questions.forEach((q) => {
        const v = values[q.no] ?? EMPTY_ANSWER;
        if (q.qtype === "single" || q.qtype === "multi") {
          v.opts.forEach((o) =>
            fields.push([`ans[${q.no}]${q.qtype === "multi" ? "[]" : ""}`, String(o)])
          );
        } else if (q.qtype === "text") {
          if (v.text.trim()) fields.push([`txt[${q.no}]`, v.text.trim()]);
        } else if (q.qtype === "scale") {
          if (v.score) fields.push([`score[${q.no}]`, String(v.score)]);
        }
      });
      const res = await submitVote(Number(no), fields);
      toast(res.msg || "참여해 주셔서 감사합니다.", "success");
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "참여에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  }, [no, data, member, requireLogin, questions, values, toast, reload]);

  if (loading) {
    return (
      <>
        <PageBanner
          title="투표·설문"
          breadcrumb={[{ label: "투표·설문", to: "/vote" }, { label: "참여" }]}
        />
        <section className="mx-auto max-w-3xl space-y-4 px-4 py-10 md:px-6">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-52 w-full rounded-xl" />
        </section>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageBanner
          title="투표·설문"
          breadcrumb={[{ label: "투표·설문", to: "/vote" }, { label: "참여" }]}
        />
        <section className="mx-auto max-w-3xl px-4 py-12 md:px-6">
          <EmptyState
            icon="ri-error-warning-line"
            title="투표를 불러오지 못했습니다"
            description={error ?? "존재하지 않는 투표입니다."}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </section>
      </>
    );
  }

  return (
    <>
      <PageBanner
        title={typeTxt}
        breadcrumb={[{ label: "투표·설문", to: "/vote" }, { label: typeTxt }]}
      />

      <section className="mx-auto max-w-3xl px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
            {typeTxt}
          </span>
          <span
            className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATE_BADGE[data.state]}`}
          >
            {STATE_LABEL[data.state]}
          </span>
          {data.is_anon === 1 && (
            <span className="inline-block rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
              익명
            </span>
          )}
          {joined && (
            <span className="inline-block rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-900">
              참여완료
            </span>
          )}
        </div>

        <h1 className="mt-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          {data.subject || data.title || "투표"}
        </h1>
        {data.content && (
          <p className="mt-2 whitespace-pre-wrap text-sm text-foreground-600">
            {data.content}
          </p>
        )}

        {/* 기간 · 참여 · 카운트다운 */}
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-background-200 bg-background-100/60 px-4 py-3 text-sm text-foreground-600">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-calendar-line" aria-hidden="true"></i>
            {formatDateTime(data.start_date)} ~ {formatDateTime(data.end_date)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-group-line" aria-hidden="true"></i>
            참여 <strong className="text-foreground-900">{data.join_cnt}</strong>명
          </span>
          {open && (
            <span className="ml-auto inline-flex items-center gap-1.5">
              종료까지{" "}
              <strong className="font-heading tabular-nums text-accent-700">
                {formatCountdown(endRemaining)}
              </strong>
            </span>
          )}
          {data.state === "ready" && (
            <span className="ml-auto inline-flex items-center gap-1.5">
              시작까지{" "}
              <strong className="font-heading tabular-nums text-foreground-700">
                {formatCountdown(startRemaining)}
              </strong>
            </span>
          )}
        </div>

        {data.is_anon === 1 && (
          <p className="mt-3 flex items-start gap-2 text-sm text-foreground-500">
            <i className="ri-shield-user-line mt-0.5" aria-hidden="true"></i>
            익명으로 진행됩니다. 누가 골랐는지 아무도 볼 수 없습니다.
          </p>
        )}

        {/* 참여 폼 */}
        {showForm && (
          <form
            className="mt-6"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <ul className="space-y-4">
              {questions.map((q, i) => (
                <VoteQuestionBlock
                  key={q.no}
                  index={i}
                  question={q}
                  value={values[q.no] ?? EMPTY_ANSWER}
                  onChange={(next) =>
                    setValues((prev) => ({ ...prev, [q.no]: next }))
                  }
                />
              ))}
            </ul>

            {formError && (
              <p className="mt-4 flex items-center gap-1.5 rounded-lg bg-accent-50 px-3 py-2 text-sm font-medium text-accent-800">
                <i className="ri-error-warning-line" aria-hidden="true"></i>
                {formError}
              </p>
            )}

            <div className="sticky bottom-[var(--tabbar-h)] z-40 mt-6 xl:bottom-0">
              <div className="rounded-xl border border-background-200 bg-background-50/95 p-3 backdrop-blur">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full whitespace-nowrap rounded-md bg-primary-500 py-3.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  {submitting ? "제출 중…" : joined ? "응답 수정" : "참여하기"}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* 참여 불가 안내 */}
        {!showForm && joined && !canChange && (
          <p className="mt-6 flex items-center gap-2 rounded-xl border border-background-200 bg-background-100 px-4 py-3.5 text-sm text-foreground-600">
            <i className="ri-lock-line" aria-hidden="true"></i>
            이미 참여하셨습니다. 응답은 변경할 수 없습니다.
          </p>
        )}
        {!open && data.state === "ready" && (
          <p className="mt-6 flex items-center gap-2 rounded-xl border border-background-200 bg-background-100 px-4 py-3.5 text-sm text-foreground-600">
            <i className="ri-time-line" aria-hidden="true"></i>
            아직 시작 전입니다. {formatDateTime(data.start_date)} 부터 참여할 수 있습니다.
          </p>
        )}
        {!open && data.state === "closed" && !joined && (
          <p className="mt-6 flex items-center gap-2 rounded-xl border border-background-200 bg-background-100 px-4 py-3.5 text-sm text-foreground-600">
            <i className="ri-lock-line" aria-hidden="true"></i>
            마감되었습니다.
          </p>
        )}

        {/* 결과 */}
        <div className="mt-8">
          <h2 className="font-heading text-lg font-bold text-foreground-950">결과</h2>
          <div className="mt-3">
            {canResult ? (
              <VoteResult data={data} />
            ) : (
              <p className="flex items-center gap-2 rounded-xl border border-background-200 bg-background-100 px-4 py-4 text-sm text-foreground-600">
                <i className="ri-time-line text-foreground-400" aria-hidden="true"></i>
                결과는 {formatDateTime(data.end_date)} 이후에 공개됩니다.
              </p>
            )}
          </div>
        </div>

        {/* 목록으로 */}
        <div className="mt-8 flex justify-center">
          <Link
            to="/vote"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            목록으로
          </Link>
        </div>
      </section>
    </>
  );
}